import React, { useCallback, useMemo, useState, VFC } from 'react';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { INITIAL_ORDER_BY_FOR_HEADER } from 'src/common/components/table/sortable-column-header';
import { EventBus } from 'src/common/EventBus';
import { Pagination } from 'src/common/Pagination';
import { DashboardHomeSearch } from '../search';

import {
  DashboardHomeJourneyListHeaderSortType,
  DashboardHomeJourneysList,
  DashboardHomeJourneysListProps,
} from './list';
import ActionBar from 'src/common/components/library/action-bar';

export interface DashboardHomeJourneysPropsInterface
  extends Pick<DashboardHomeJourneysListProps, 'journeys' | 'searchInputValue' | 'activeTableSortType'> {
  isListLoading: boolean;
  hideDashboardContent: boolean;
  pagination: { currentPage: number; pages: number };
}

export const DashboardHomeJourneys: VFC<DashboardHomeJourneysPropsInterface> = ({
  journeys,
  pagination,
  hideDashboardContent,
  searchInputValue,
  activeTableSortType,
  isListLoading,
}) => {
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const handleConfirmDelete = useCallback(() => {
    EventBus.emit('home:journey-bulk-delete', Array.from(selectedRows));
    setSelectedRows(new Set());
  }, [selectedRows]);

  const onClearSelection = useCallback(() => setSelectedRows(new Set()), []);

  const onSearchInputChanged = useCallback((value: string) => {
    EventBus.emit('home:journey-search', value);
  }, []);

  const onCurrentPageChanged = useCallback((_, newPage: number) => {
    EventBus.emit('home:journey-page-change', newPage);
  }, []);

  const onTagsFilterChanged = useCallback((tags: string) => {
    EventBus.emit('home:journey-tags-change', tags);
  }, []);

  const onTableHeaderClicked = useCallback(
    (sortStack: DashboardHomeJourneyListHeaderSortType) => {
      EventBus.emit('home:journey-sort-change', {
        sortName: sortStack.sortName,
        orderBy: activeTableSortType.sortName !== sortStack.sortName ? INITIAL_ORDER_BY_FOR_HEADER : sortStack.orderBy,
      });
    },
    [activeTableSortType.sortName]
  );

  const isListEmpty = useMemo(() => journeys.length === 0, [journeys]);

  const renderContent = () => {
    if (isListLoading) {
      return <FixedLoadingSpinner />;
    }

    return (
      <div className='flex relative flex-col justify-between w-full space-y-md'>
        {!hideDashboardContent && (
          <>
            <div className='mt-10 flex items-center gap-4'>
              <div className='px-2 py-1 select-none'>
                <span className='text-bedrock-p-strong text-bedrock-black'>All Journeys</span>
              </div>
              <div className='w-px self-stretch rounded-md bg-bedrock-gray-medium' />
              <DashboardHomeSearch
                searchInputValue={searchInputValue}
                onSearchInputChange={onSearchInputChanged}
                onTagsChange={onTagsFilterChanged}
              />
            </div>
            <ActionBar
              selectedCount={selectedRows.size}
              onClearSelection={onClearSelection}
              handleConfirmDelete={handleConfirmDelete}
            />
            <DashboardHomeJourneysList
              activeTableSortType={activeTableSortType}
              onTableHeaderClick={onTableHeaderClicked}
              isListEmpty={isListEmpty}
              searchInputValue={searchInputValue}
              journeys={journeys}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
            />
          </>
        )}
        {hideDashboardContent && (
          <div className='mt-lg max-w-[640px] text-bedrock-p text-bedrock-black'>
            Once you’ve created your first Journey we’ll list them here and show you some neat analytics - Go ahead and
            create your first Journey.
          </div>
        )}
        {!isListLoading && (
          <div className='mt-6'>
            <Pagination count={pagination.pages} page={pagination.currentPage} onChange={onCurrentPageChanged} />
          </div>
        )}
      </div>
    );
  };

  return renderContent();
};
